import React, { useState } from "react";
import { composeDecorators } from "@draft-js-plugins/editor";
import createToolbarPlugin from "@draft-js-plugins/static-toolbar";
import createFocusPlugin from "@draft-js-plugins/focus";
import createLinkifyPlugin from "@draft-js-plugins/linkify";
import createLinkPlugin from "@draft-js-plugins/anchor";
import createCounterPlugin from "@draft-js-plugins/counter";
import createAlignmentPlugin from "@draft-js-plugins/alignment";
import createMentionPlugin, {
  defaultSuggestionsFilter
} from "@draft-js-plugins/mention";
import {
  ItalicButton,
  BoldButton,
  UnderlineButton
} from "@draft-js-plugins/buttons";
import makeStyles from "@mui/styles/makeStyles";
import textEditorStyles, {
  toolbarStyles,
  toolbarButtonStyles
} from "assets/jss/default/textEditorStyles";
import DefaultMentionSuggestions from "components/TextEditors/DefaultMentionSuggestions";
import createTextLengthLimiterPlugin from "components/TextEditors/TextLengthLimiterPlugin";

const useTextEditorStyles = makeStyles(textEditorStyles);
const useToolbarStyles = makeStyles(toolbarStyles);
const useToolbarButtonStyles = makeStyles(toolbarButtonStyles);

function createStaticToolbar({ LinkButton, Toolbar }) {
  return function StaticToolbar(props) {
    return (
      <Toolbar {...props}>
        {externalProps => (
          <>
            <BoldButton {...externalProps} />
            <ItalicButton {...externalProps} />
            <UnderlineButton {...externalProps} />
            <LinkButton {...externalProps} />
          </>
        )}
      </Toolbar>
    );
  };
}

function useTextEditorPlugins(maxLength = 0, isExtendedEditor = false) {
  if (maxLength < 0) {
    maxLength = 0;
  }
  const classes = useTextEditorStyles({ isExtendedEditor });
  const toolbarClasses = useToolbarStyles({ isExtendedEditor });
  const toolbarButtonClasses = useToolbarButtonStyles();
  const [{ plugins, components, decorator }] = useState(() => {
    const mentionPlugin = createMentionPlugin({
      supportWhitespace: true,
      entityMutability: "IMMUTABLE"
    });
    const alignmentPlugin = createAlignmentPlugin();
    const counterPlugin = createCounterPlugin();
    const toolbarPlugin = createToolbarPlugin({
      theme: {
        toolbarStyles: toolbarClasses,
        buttonStyles: toolbarButtonClasses
      }
    });
    const focusPlugin = createFocusPlugin();
    const linkifyPlugin = createLinkifyPlugin();
    const anchorPlugin = createLinkPlugin();
    const maxLengthReachedAction = () => {}; // noop for now, possibility: shake animation via react-spring
    const textLengthLimiterPlugin = createTextLengthLimiterPlugin(
      maxLength,
      maxLengthReachedAction
    );

    const { Toolbar } = toolbarPlugin;
    const { MentionSuggestions } = mentionPlugin;
    const { LinkButton } = anchorPlugin;
    const { CharCounter } = counterPlugin;

    const decorator = composeDecorators(
      focusPlugin.decorator,
      alignmentPlugin.decorator
    );

    const editorPlugins = [
      mentionPlugin,
      toolbarPlugin,
      focusPlugin,
      alignmentPlugin,
      linkifyPlugin,
      textLengthLimiterPlugin
    ];

    if (maxLength > 0) {
      editorPlugins.push(counterPlugin);
    }

    return {
      decorator,
      plugins: editorPlugins,
      components: {
        Toolbar,
        MentionSuggestions: function DefaultMentionSuggestionsWrapper({
          // eslint-disable-next-line react/prop-types
          disabled
        }) {
          return (
            <DefaultMentionSuggestions
              suggestionsFilter={defaultSuggestionsFilter}
              disabled={disabled}
              mentionSuggestionsComponent={MentionSuggestions}
            />
          );
        },
        LinkButton,
        StaticToolbar: createStaticToolbar({ LinkButton, Toolbar }),
        CharCounter
      }
    };
  });

  return {
    plugins,
    components,
    decorator,
    classes
  };
}

export { defaultSuggestionsFilter, useTextEditorPlugins };
